import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@cowbell/ui/core';
import styles from './EmptyState.module.css';

/** Lines shown before the description is cut off. */
export const DESCRIPTION_LINES = 3;

const MORE_LABEL = 'Show more';
/** What follows the cut text; measured with it so the link fits too. */
const CUT_SUFFIX = `… ${MORE_LABEL}`;

/**
 * True when the text is taller than DESCRIPTION_LINES. Sub-pixel rounding
 * does not count, and an unknown line height never counts as cut off.
 */
export function needsFold(fullHeight: number, lineHeight: number): boolean {
  if (!(lineHeight > 0)) return false;
  return fullHeight > lineHeight * DESCRIPTION_LINES + 1;
}

/**
 * How many words to show before the "… Show more" link, or null when the
 * whole text fits. Binary search, since each check is a layout read.
 */
export function wordsThatFit(
  words: readonly string[],
  fits: (candidate: string) => boolean
): number | null {
  if (fits(words.join(' '))) return null;
  let lo = 0;
  let hi = words.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (fits(words.slice(0, mid).join(' ') + CUT_SUFFIX)) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

interface AgentDescriptionProps {
  readonly text: string;
}

/**
 * The agent's description, cut to a few lines with an inline "Show more"
 * link at the end. The link only appears when the text does not fit.
 * Callers key this by agent so a new agent starts collapsed.
 */
export function AgentDescription({ text }: AgentDescriptionProps) {
  const words = useMemo(() => text.trim().split(/\s+/), [text]);
  const wrapRef = useRef<HTMLDivElement>(null);
  // A hidden copy at the same width, used to try candidate cuts without
  // touching what the user sees.
  const probeRef = useRef<HTMLParagraphElement>(null);
  const [shownWords, setShownWords] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const probe = probeRef.current;
    if (!wrap || !probe) return;
    const measure = () => {
      const lineHeight = Number.parseFloat(getComputedStyle(probe).lineHeight);
      const fits = (candidate: string) => {
        probe.textContent = candidate;
        return !needsFold(probe.scrollHeight, lineHeight);
      };
      setShownWords(wordsThatFit(words, fits));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [words]);

  const isCut = shownWords !== null;

  return (
    <div ref={wrapRef} className={styles.descriptionWrap}>
      <Text className={styles.description}>
        {isCut && !isExpanded
          ? `${words.slice(0, shownWords).join(' ')}… `
          : `${text} `}
        {isCut ? (
          <button
            type="button"
            className={styles.descriptionLink}
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? 'Show less' : MORE_LABEL}
          </button>
        ) : null}
      </Text>
      <Text
        ref={probeRef}
        className={`${styles.description} ${styles.descriptionProbe}`}
        aria-hidden="true"
      />
    </div>
  );
}
